import { RouterStore } from '@superwf/mobx-react-router';
import { action, computed, observable } from 'mobx';
import { parse, stringify } from 'qs';
import { SyntheticEvent } from 'react';
import ErrorUtil from '../util/ErrorUtil';
import {
  filterForwardParamsForStartExternalAuth,
  filterForwardParamsFromCurrentParams,
  filterForwardParamsFromCurrentParamsReturningParameters,
  preserveParams,
} from '../util/ForwardParamsUtil';
import { extractHyperlink, hasCurrentHost, prefixWithSlash } from '../util/NavigationUtil';
import GaDataStore from './GaDataStore';
import { ALLOWED_LANGUAGES, Language } from './LanguageStore';
import RememberMeStore from './RememberMeStore';
import { RootStore } from './RootStore';

class RouteStore {
  @observable accessor redirecting: boolean = false;

  private routing: RouterStore;
  private rememberMeStore: RememberMeStore;

  constructor(rootStore: RootStore) {
    this.routing = rootStore.router;
    this.rememberMeStore = rootStore.rememberMeStore;
    if (!window.telia?.navigate) {
      this.setupGlobalRouting();
    }
  }

  @computed
  get pathname() {
    return this.getLocation().pathname || '/';
  }

  @computed
  get path() {
    return this.toPath(this.pathname);
  }

  @computed
  get params() {
    return this.getLocation().search;
  }

  @computed
  get hash() {
    return this.getLocation().hash?.slice(1);
  }

  public getParameter = (paramName: string) => {
    return parse(this.params, { ignoreQueryPrefix: true })[paramName];
  };

  @computed
  get formForwardParams() {
    return filterForwardParamsFromCurrentParams({ ...this.decodedParams, rememberMe: this.rememberMeStore.rememberMe });
  }

  @computed
  get formForwardParamsForStartExternalAuth() {
    return filterForwardParamsForStartExternalAuth({ ...this.decodedParams, rememberMe: this.rememberMeStore.rememberMe });
  }

  @computed
  get decodedParams(): any {
    const charset = this.params.includes('service=ArgosService') ? 'iso-8859-1' : 'utf-8';
    return parse(this.params, { ignoreQueryPrefix: true, charset });
  }

  @computed
  get langFromParams(): string {
    return this.getParameter('locale') as string;
  }

  @action.bound
  public setToRedirect() {
    this.redirecting = true;
  }

  @action.bound
  public setLanguage(locale: Language) {
    if (!ALLOWED_LANGUAGES.includes(locale)) {
      ErrorUtil.pushErrorToTeliaRumAndLog(new Error(`Unsupported language: ${locale})`));
    } else {
      this.routing.push(this.formForwardParamsAnd({ locale }, ['locale']));
    }
  }

  @action.bound
  public navigate(event: Event | SyntheticEvent) {
    if (this.isLocalNavigation(event)) {
      this.navigateLocal(event);
    }
  }

  public hasModifierKey = (event: MouseEvent) => {
    return !!(event.ctrlKey || event.altKey || event.shiftKey || event.metaKey);
  };

  @action.bound
  public navigateLocal(event: Event | SyntheticEvent) {
    if (this.hasModifierKey(event as MouseEvent) || event.currentTarget?.target === '_blank' || !event.currentTarget) {
      return;
    }
    event.preventDefault();
    GaDataStore.push({ event: 'react-pageview' });
    const target: EventTarget = event.currentTarget;
    const url = target.pathname + target.search + target.hash;
    if (window.parent === window) {
      this.routing.push(url);
    } else {
      this.routing.replace(url);
    }
    this.redirecting = false;
  }

  public toPath(pathname: string) {
    return pathname.split('/').filter((path) => path);
  }

  public formForwardParamsAnd(params: { [key: string]: string }, exclude?: string[]) {
    const forwardParams = filterForwardParamsFromCurrentParamsReturningParameters(
      {
        ...this.decodedParams,
        rememberMe: this.rememberMeStore.rememberMe,
      },
      exclude,
    );
    return `${stringify({ ...forwardParams, ...params }, { addQueryPrefix: true, skipNulls: true })}`;
  }

  public preserveParams(preserve: string[], params?: { [key: string]: string }) {
    const forwardParams = preserveParams({ ...this.decodedParams, rememberMe: this.rememberMeStore.rememberMe }, preserve);
    return params ? `${forwardParams}${!forwardParams ? '?' : '&'}${stringify(params)}` : forwardParams;
  }

  private getLocation = () => this.routing.location || window.location;

  @action.bound
  private push(url: string) {
    const link = extractHyperlink(url);

    GaDataStore.push({ event: 'react-pageview' });
    if (hasCurrentHost(link)) {
      this.routing.push(prefixWithSlash(link.pathname) + link.search + link.hash);
    } else {
      window.location.assign(link.href);
    }
  }

  public historyLength() {
    return this.routing.pathList.length;
  }

  @action.bound
  public historyReplace(url: string) {
    this.routing.history.replace(url);
  }

  @action.bound
  public replace(url: string) {
    GaDataStore.push({ event: 'react-pageview' });
    const htmlLink = document.createElement('a');
    htmlLink.href = url;
    if (hasCurrentHost(htmlLink)) {
      this.routing.replace(htmlLink.pathname + htmlLink.search + htmlLink.hash);
    } else {
      window.location.replace(url);
    }
  }

  @action.bound
  public route(url: string, forceReplace: boolean = false) {
    if (!forceReplace && window.parent === window) {
      return this.push(url);
    }
    return this.replace(url);
  }

  @computed
  get homePageWithParams() {
    return `/${this.formForwardParams}`;
  }

  private setupGlobalRouting() {
    if (!window.telia) {
      window.telia = {};
    }
    window.telia.navigateLocal = this.navigateLocal;
    window.telia.navigate = this.navigate;
  }

  private isLocalNavigation = (event: Event | SyntheticEvent) => {
    const { currentTarget } = event;
    return !currentTarget || hasCurrentHost(currentTarget);
  };
}

export default RouteStore;
